import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const useRealTimeUpdates = (user) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastCheckRef = useRef(Date.now());

  const checkForUpdates = useCallback(async () => {
    if (!user) return;

    try {
      const [alertsRes, docsRes] = await Promise.all([
        axios.get(`${API}/alerts`, {}).catch(() => ({ data: [] })),
        axios.get(`${API}/documents`, {}).catch(() => ({ data: [] })),
      ]);

      const cutoff = lastCheckRef.current;
      const newAlerts = alertsRes.data.filter(
        (alert) => new Date(alert.created_at) > new Date(cutoff)
      );
      const newDocs = docsRes.data.filter(
        (doc) => new Date(doc.created_at) > new Date(cutoff)
      );

      const newNotifications = [];

      newAlerts.forEach((alert) => {
        newNotifications.push({
          id: `alert-${alert.id}`,
          type: 'alert',
          title: alert.title,
          message: alert.description,
          timestamp: alert.created_at,
          severity: alert.severity,
          read: false,
        });
      });

      newDocs.forEach((doc) => {
        newNotifications.push({
          id: `doc-${doc.id}`,
          type: 'document',
          title: 'Documento Generado',
          message: doc.title,
          timestamp: doc.created_at,
          read: false,
        });
      });

      if (newNotifications.length > 0) {
        setNotifications((prev) => [...newNotifications, ...prev]);
        setUnreadCount((prev) => prev + newNotifications.length);
      }

      lastCheckRef.current = Date.now();
    } catch (error) {
      // Silently fail for polling
    }
  }, [user]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications((prev) =>
      prev.map((notif) => ({ ...notif, read: true }))
    );
    setUnreadCount(0);
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Poll for updates every 30 seconds
  useEffect(() => {
    if (!user) return;

    // Initial check
    checkForUpdates();

    // Set up polling interval
    const interval = setInterval(checkForUpdates, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [user, checkForUpdates]);

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAll,
    refresh: checkForUpdates,
  };
};

export default useRealTimeUpdates;
